import { I18n } from "@assets/strings";
import React, { useEffect, useState } from "react";
import { Redirect } from "@core/routing/redirect";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useApplication } from "@domain/application/hooks";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { useUserProfile } from "@domain/user/hooks";
import { ApplicationStep } from "@ui/components/application/applicationStep";
import { ApplicationStoryCard } from "@ui/components/application/applicationStoryCard";
import { SummarySectionView } from "@ui/components/application/summaryView";
import { PrimaryMedium } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { row } from "@ui/components/layout";
import { Page } from "@ui/components/page";
import { theme } from "@ui/styles/theme";
import { gap, mobile } from "@ui/styles/utils";

interface ApplicationSheetPageProps extends React.HTMLAttributes<HTMLDivElement> {
	profileId?: string;
	profileSlug?: string;
}

export const ApplicationSheetPage: React.FC<ApplicationSheetPageProps> = ({ profileId, profileSlug, ...htmlProps }) => {
	if (!profileId && !profileSlug) {
		return <Redirect to={Path.Root} />;
	}
	const currentProfile = useCurrentProfile();
	const { result: profile, loading } = useUserProfile(profileId ?? "", profileSlug);
	const [applicationId, setApplicationId] = useState("");

	useEffect(() => {
		if (profile?.applicationId) {
			setApplicationId(profile.applicationId);
		}
	}, [profile?.applicationId]);

	const { result: application, loading: isApplicationLoading } = useApplication(applicationId);

	const isAdmin =
		currentProfile?.adminRoles &&
		(currentProfile.adminRoles[0] === "ADMIN" || currentProfile?.adminRoles[0] === "SYS_ADMIN");

	return (
		<Container {...htmlProps}>
			<ContentBlock>
				<Title>{I18n.t("program.page.full_title")}</Title>
				{profile && currentProfile && currentProfile.adminRoles && (
					<SyledPrimaryMedium>
						{I18n.t(isAdmin ? "program.sheet.card.admin-title" : "program.sheet.card.title")}
						<span style={{ fontWeight: 700 }}>{profile?.primaryName}</span>
					</SyledPrimaryMedium>
				)}

				{!isApplicationLoading && !!application && !!profile ? (
					<LeftBloc>
						<SummarySectionView
							isForApplicationProcess={false}
							application={application}
							renderIcons={() => <ApplicationStep isOk={true} />}
						/>
					</LeftBloc>
				) : (
					<ClipLoader />
				)}
			</ContentBlock>
			{loading ? (
				<ClipLoader />
			) : profile ? (
				<RigthBlockWrapper>
					<ApplicationStoryCard profile={profile} />
				</RigthBlockWrapper>
			) : null}
		</Container>
	);
};

const Container = styled(Page)`
	display: grid;
	grid-template-columns: minmax(0, 1fr) 350px;
	grid-template-rows: auto;
	grid-column-gap: 45px;
	// grid-row-gap: 24px;
	grid-template-areas:
		"content vote-card"
		"content ."
		"content .";

	@media ${mobile} {
		grid-template-columns: 100%;

		grid-template-areas:
			"content"
			"vote-card";
	}
`;

const ContentBlock = styled.div`
	grid-area: content;
	width: 100%;
	${gap(20)};
	@media ${mobile} {
		padding: 22px 0px;
	}
`;

const LeftBloc = styled(RoundedBlock)`
	padding: 40px 31px 40px 36px;
	width: 100%;

	@media ${mobile} {
		padding: 40px 10px 40px 10px;
	}
`;

const Title = styled.div`
	${row(16, "flex-start", "baseline")};
	${({ theme }) => theme.textStyles.subtitle};
`;

const RigthBlockWrapper = styled.div`
	padding: 84px 0px;
	grid-area: vote-card;
	position: sticky;
	${gap(20)};
`;

const SyledPrimaryMedium = styled(PrimaryMedium)`
	color: ${theme.colors.secondary};
`;
